import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { List, Typography, Form, Col } from 'antd'
import Cameo from 'pages/CarePlanBuilder/components/Cameo/Cameo'
import capitalize from 'lodash.capitalize'
import { getInitials } from 'utils/helpers'
import RoadmapV2 from 'model/carePlans/carePlan'
import useDebouncedFunction from 'utils/hooks/useDebouncedFunction'
import dayjs from 'dayjs'
import { TextArea } from 'ui/styled'
import useUpdateRoadmapEntity from 'apollo/mutations/CarePlan/UPDATE_CAREPLAN_ENTITY'
import useRoadmapUpdate from 'apollo/mutations/CarePlan/CAREPLAN_UPDATE'
import {
  Hint,
  ListItemCentered,
  ListItemMetaCentered,
  FormWrapper
} from 'pages/CarePlanBuilder/steps/NoteStep/styled'
import NoteTemplates from 'pages/CarePlanBuilder/steps/NoteStep/NoteTemplates'

const { Title } = Typography
const { Item } = Form

const NoteStep = ({ carePlan }) => {
  const {
    id,
    suggested_doctors,
    suggested_facilities,
    isFacility,
    performed_by,
    introduction
  } = carePlan

  const [notesMessage, setNotesMessage] = useState('')
  const [introMessage, setIntroMessage] = useState('')
  const [intro, setIntro] = useState(introduction)
  const [form] = Form.useForm()

  const { onUpdateRoadmapEntity } = useUpdateRoadmapEntity()
  const { onRoadmapUpdate } = useRoadmapUpdate()

  const updateEntityNote = useCallback(
    async (entity, navigator_notes) => {
      const { id: entity_id } = entity

      const { isErrorFree } = await onUpdateRoadmapEntity({
        entity_type: isFacility ? 'Facility' : 'Provider',
        roadmap_id: id,
        entity_id,
        attributes: {
          navigator_notes
        }
      })
      if (isErrorFree) {
        const autosaveTime = dayjs().format('HH:mm:ss')
        setNotesMessage(`Notes autosaved at ${autosaveTime}`)
      }
    },
    [onUpdateRoadmapEntity, id, isFacility, setNotesMessage]
  )

  const updateCarePlanIntro = useCallback(
    async value => {
      setIntro(value)

      const { isErrorFree } = await onRoadmapUpdate({
        id,
        attributes: { introduction: value }
      })
      if (isErrorFree) {
        const autosaveTime = dayjs().format('HH:mm:ss')
        setIntroMessage(`Intro autosaved at ${autosaveTime}`)
      }
    },
    [onRoadmapUpdate, id, setIntro, setIntroMessage]
  )

  const updateEntityNoteDebounced = useDebouncedFunction(updateEntityNote, 1000)
  const updateCarePlanIntroDebounced = useDebouncedFunction(
    updateCarePlanIntro,
    1000
  )

  const intro_max_length = 155

  const entitiesData = isFacility ? suggested_facilities : suggested_doctors
  const entities = entitiesData.map(({ profile, cost, navigator_notes }) => ({
    ...profile,
    ...cost,
    navigator_notes
  }))

  const entity_type = capitalize(performed_by)

  const hintText = `${`${
    intro?.length || 0
  } of ${intro_max_length} characters`}`
  const entityPlaceholder = `Describe why you selected this ${
    isFacility ? 'facility' : 'provider'
  } (optional)`

  const onTemplateSelect = note => {
    form.setFieldsValue({ intro: note })
    updateCarePlanIntroDebounced(note)
  }

  return (
    <FormWrapper>
      <Form form={form}>
        <Item name="intro" initialValue={introduction} style={{ margin: 0 }}>
          <TextArea
            placeholder="Introduction text"
            maxLength={intro_max_length}
            rows={6}
            onChange={({ target: { value } }) =>
              updateCarePlanIntroDebounced(value)
            }
          />
        </Item>
        {introMessage}
      </Form>

      <NoteTemplates intro={intro} onTemplateSelect={onTemplateSelect} />
      <Hint>{hintText}</Hint>
      {!!entities.length && <Title level={3}>{entity_type} Annotation</Title>}
      <List>
        {entities.map(entity => {
          const initials = getInitials(entity)
          return (
            <ListItemCentered key={entity.uuid}>
              <ListItemMetaCentered
                title={isFacility ? entity.name : entity.full_name}
                avatar={
                  !isFacility && (
                    <Cameo
                      initials={initials}
                      image_url={entity.image_url}
                      showCheckbox={false}
                      border_color="#000"
                    />
                  )
                }
              />
              <Col>
                <TextArea
                  defaultValue={entity.navigator_notes}
                  placeholder={entityPlaceholder}
                  rows={4}
                  onChange={({ target: { value } }) =>
                    updateEntityNoteDebounced(entity, value)
                  }
                />
                <div>{notesMessage}</div>
              </Col>
            </ListItemCentered>
          )
        })}
      </List>
    </FormWrapper>
  )
}

NoteStep.propTypes = {
  carePlan: PropTypes.shape(RoadmapV2.shape).isRequired
}

export default NoteStep
